import { RING_RADIUS } from '@circle/shared';
import { defaultTokens } from './tokens';

/**
 * Radii used by the default theme, in world units. The ring sits at
 * RING_RADIUS; day ticks point inward, month labels ride an arc outside it.
 */
export const M = {
  ring: RING_RADIUS,
  /** Inner edge of the month band / day tick gutter. */
  bandInner: RING_RADIUS - 40,
  /** Outer edge of the month band, past the labels. */
  bandOuter: RING_RADIUS + 92,
  dayTick: RING_RADIUS - 13,
  weekendTick: RING_RADIUS - 8,
  monthTick: RING_RADIUS - 40,
  labelArc: RING_RADIUS + 34,
  labelSize: 34,
  markRadius: RING_RADIUS + 12,
  todayInner: RING_RADIUS * 0.14,
  todayOuter: RING_RADIUS + 118,
} as const;

/**
 * Float card sizes in SCREEN pixels, read back out of the theme tokens so the
 * fragments and the scene's layout pass cannot drift apart. Changing a token
 * changes both the drawing and the packing extents.
 */
export const FLOAT = {
  width: Number(defaultTokens['--float-w']),
  height: Number(defaultTokens['--float-h']),
  clusterWidth: Number(defaultTokens['--cluster-w']),
  clusterHeight: Number(defaultTokens['--cluster-h']),
} as const;

/**
 * The same drawing at any ring size.
 *
 * Every world-unit number in {@link M} is a fraction of `RING_RADIUS`, so a
 * ring drawn at radius `r` is the default theme scaled by `r / RING_RADIUS`:
 * ticks keep their share of the gutter, the month band keeps its margin, and a
 * quarter-size circle reads as the same drawing rather than as a ring with
 * enormous labels. At the full radius `M` itself comes back, so a calendar with
 * no circles is drawn from the identical numbers it always was.
 */
export type Metrics = { -readonly [K in keyof typeof M]: number };

export function metricsAt(radius: number): Metrics {
  const k = Number.isFinite(radius) && radius > 0 ? radius / RING_RADIUS : 1;
  if (k === 1) return M;
  return {
    ring: M.ring * k,
    bandInner: M.bandInner * k,
    bandOuter: M.bandOuter * k,
    dayTick: M.dayTick * k,
    weekendTick: M.weekendTick * k,
    monthTick: M.monthTick * k,
    labelArc: M.labelArc * k,
    labelSize: M.labelSize * k,
    markRadius: M.markRadius * k,
    todayInner: M.todayInner * k,
    todayOuter: M.todayOuter * k,
  };
}

/** Hairline widths are in *screen* px: every stroke uses non-scaling-stroke. */
export const W = {
  hairline: 0.6,
  ring: 1.4,
  day: 1,
  monthBoundary: 1,
  today: 1.2,
} as const;
